import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Save, X, Copy } from 'lucide-react';
import { plantillaArticuloService } from '../../services/api';
import PlantillaArticuloCanvas from './PlantillaArticuloCanvas';

/**
 * PlantillasArticuloEditorPage — administración de plantillas de artículo.
 *
 * Layout:
 *   ┌──────────────────┬───────────────────────────────────────┐
 *   │ Lista            │  Editor de la plantilla activa        │
 *   │ + Nueva          │  (metadatos + canvas de elementos)    │
 *   └──────────────────┴───────────────────────────────────────┘
 *
 * Cada plantilla define un grid interno (12×12 default) donde se ubican
 * elementos: categoría, título, resumen, imagen, cuerpo, autor, separador,
 * espacio. El editor visual permite arrastrar y redimensionar.
 */
export default function PlantillasArticuloEditorPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [activa, setActiva] = useState(null); // {id?, nombre, ..., elementos}
  const [guardando, setGuardando] = useState(false);

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const list = await plantillaArticuloService.listar(false);
      setPlantillas(list);
    } catch (err) {
      console.error(err);
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { recargar(); }, [recargar]);

  const nuevaPlantilla = () => {
    setActiva({
      id: null,
      nombre: '',
      descripcion: '',
      columnas: 12,
      filas: 12,
      columnasCuerpo: 1,
      activa: true,
      elementos: [],
    });
  };

  const editarPlantilla = async (id) => {
    try {
      const p = await plantillaArticuloService.obtener(id);
      if (!p) return;
      setActiva({
        ...p,
        elementos: (p.elementos || []).map((e, i) => ({ ...e, _key: `db-${e.id ?? i}` })),
      });
    } catch (err) {
      alert('No se pudo cargar: ' + err.message);
    }
  };

  const duplicarPlantilla = (p) => {
    setActiva({
      id: null,
      nombre: `${p.nombre} (copia)`,
      descripcion: p.descripcion,
      columnas: p.columnas,
      filas: p.filas,
      columnasCuerpo: p.columnasCuerpo || 1,
      activa: true,
      elementos: (p.elementos || []).map((e, i) => ({ ...e, id: null, _key: `dup-${i}` })),
    });
  };

  const eliminarPlantilla = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla? Los bloques que la usen volverán al render estándar.')) return;
    try {
      await plantillaArticuloService.eliminar(id);
      await recargar();
      if (activa?.id === id) setActiva(null);
    } catch (err) {
      alert('No se pudo eliminar: ' + err.message);
    }
  };

  const guardar = async () => {
    if (!activa) return;
    if (!activa.nombre.trim()) { alert('El nombre es obligatorio.'); return; }
    if (activa.elementos.length === 0) { alert('Añade al menos un elemento a la plantilla.'); return; }
    setGuardando(true);
    try {
      const dto = {
        nombre: activa.nombre.trim(),
        descripcion: activa.descripcion || null,
        columnas: activa.columnas,
        filas: activa.filas,
        columnasCuerpo: activa.columnasCuerpo || 1,
        activa: activa.activa,
        elementos: activa.elementos.map(({ _key, id, ...rest }) => rest),
      };
      const guardada = activa.id
        ? await plantillaArticuloService.actualizar(activa.id, dto)
        : await plantillaArticuloService.crear(dto);
      await recargar();
      setActiva({
        ...guardada,
        elementos: (guardada.elementos || []).map((e, i) => ({ ...e, _key: `db-${e.id ?? i}` })),
      });
    } catch (err) {
      alert('No se pudo guardar: ' + err.message);
    } finally { setGuardando(false); }
  };

  return (
    <div style={{ display:'flex', gap:12, height:'75vh' }}>
      {/* Lista de plantillas */}
      <aside style={{
        width:260, background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
        padding:12, display:'flex', flexDirection:'column', gap:8, overflowY:'auto',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <div style={{ fontWeight:700, color:'#1B4332', fontSize:14 }}>
            Plantillas ({plantillas.length})
          </div>
          <button onClick={nuevaPlantilla} className="btn btn-primary" style={{ padding:'4px 8px', fontSize:11 }}>
            <Plus size={12} /> Nueva
          </button>
        </div>
        {cargando && <div style={{ fontSize:12, color:'#888' }}>Cargando…</div>}
        {plantillas.map(p => (
          <div key={p.id} style={{
            border: activa?.id === p.id ? '2px solid #1B4332' : '1px solid #ddd',
            borderRadius:4, padding:8, fontSize:12,
            background: activa?.id === p.id ? '#f0fdf4' : '#fff',
          }}>
            <div style={{ fontWeight:600, color:'#111', marginBottom:2 }}>{p.nombre}</div>
            {p.descripcion && (
              <div style={{ fontSize:10, color:'#666', marginBottom:6, lineHeight:1.3 }}>
                {p.descripcion}
              </div>
            )}
            <div style={{ display:'flex', gap:4 }}>
              <button onClick={() => editarPlantilla(p.id)} title="Editar"
                style={btnMini}>
                <Edit2 size={11} />
              </button>
              <button onClick={() => duplicarPlantilla(p)} title="Duplicar"
                style={btnMini}>
                <Copy size={11} />
              </button>
              <button onClick={() => eliminarPlantilla(p.id)} title="Eliminar"
                style={{ ...btnMini, color:'#dc2626' }}>
                <Trash2 size={11} />
              </button>
              {!p.activa && (
                <span style={{ marginLeft:'auto', fontSize:9, color:'#888', fontStyle:'italic' }}>inactiva</span>
              )}
            </div>
          </div>
        ))}
      </aside>

      {/* Editor de la plantilla activa */}
      <main style={{ flex:1, minWidth:0, background:'#fff', border:'1px solid #e5e7eb',
                     borderRadius:8, padding:12, display:'flex', flexDirection:'column', gap:8 }}>
        {!activa ? (
          <div style={{ margin:'auto', color:'#888', fontStyle:'italic' }}>
            Selecciona una plantilla de la lista o crea una nueva
          </div>
        ) : (
          <>
            {/* Metadatos */}
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <input value={activa.nombre}
                onChange={e => setActiva({ ...activa, nombre: e.target.value })}
                placeholder="Nombre de la plantilla"
                style={{ flex:'1 0 200px', padding:'6px 8px', border:'1px solid #ccc', borderRadius:4, fontSize:13 }} />
              <label style={{ fontSize:11, display:'flex', gap:4, alignItems:'center' }}>
                <input type="checkbox" checked={activa.activa}
                  onChange={e => setActiva({ ...activa, activa: e.target.checked })} />
                Activa
              </label>
              <button onClick={() => setActiva(null)} className="btn btn-outline" style={{ padding:'6px 10px', fontSize:12 }}>
                <X size={12} /> Cerrar
              </button>
              <button onClick={guardar} disabled={guardando}
                className="btn btn-primary" style={{ padding:'6px 10px', fontSize:12 }}>
                <Save size={12} /> {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
            <input value={activa.descripcion || ''}
              onChange={e => setActiva({ ...activa, descripcion: e.target.value })}
              placeholder="Descripción (opcional): cuándo usar esta plantilla"
              style={{ padding:'6px 8px', border:'1px solid #ccc', borderRadius:4, fontSize:12 }} />
            <div style={{ display:'flex', gap:8, alignItems:'center', fontSize:12 }}>
              <span>Grilla interna:</span>
              <label style={{ display:'flex', gap:4, alignItems:'center' }}>
                Columnas
                <input type="number" min={4} max={24} value={activa.columnas}
                  onChange={e => setActiva({ ...activa, columnas: parseInt(e.target.value) || 12 })}
                  style={{ width:60, padding:'4px 6px', border:'1px solid #ccc', borderRadius:4 }} />
              </label>
              <label style={{ display:'flex', gap:4, alignItems:'center' }}>
                Filas
                <input type="number" min={4} max={24} value={activa.filas}
                  onChange={e => setActiva({ ...activa, filas: parseInt(e.target.value) || 12 })}
                  style={{ width:60, padding:'4px 6px', border:'1px solid #ccc', borderRadius:4 }} />
              </label>
              <label style={{ display:'flex', gap:4, alignItems:'center' }} title="Divide el elemento cuerpo en N columnas periodísticas al renderizar el PDF">
                Col. cuerpo
                <select value={activa.columnasCuerpo || 1}
                  onChange={e => setActiva({ ...activa, columnasCuerpo: parseInt(e.target.value) || 1 })}
                  style={{ padding:'4px 6px', border:'1px solid #ccc', borderRadius:4 }}>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                </select>
              </label>
              <span style={{ color:'#888', fontSize:11 }}>
                — Los elementos escalan al tamaño real del bloque cuando se aplica.
              </span>
            </div>
            {/* Canvas editor */}
            <div style={{ flex:1, minHeight:0, border:'1px solid #e5e7eb', borderRadius:4, background:'#fafafa' }}>
              <PlantillaArticuloCanvas
                columnas={activa.columnas}
                filas={activa.filas}
                elementos={activa.elementos}
                onCambio={(nuevos) => setActiva({ ...activa, elementos: nuevos })}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const btnMini = {
  padding:'3px 6px', background:'#fff', border:'1px solid #ddd',
  borderRadius:3, cursor:'pointer', color:'#555',
};
